import React, { useContext, useMemo } from 'react'

import { FleetData, IFleetData } from '@/types'
import { useLanguages } from '@/hooks'
import { carHirePath } from '@/utils'
import { Link } from '@/i18n'

import { FleetContext, FleetProvider } from './context'
import { Sidebar } from './sidebar'
import { FleetCard } from './card'

const FleetContentView: React.FC<IFleetData> = ({ data }) => {
  const { t } = useLanguages()
  const { passengers, transmission, vehicleClasses } = useContext(FleetContext)

  const filteredData = useMemo(
    () =>
      data.filter(_v => {
        const transmissionCheck =
          transmission === 'default'
            ? true
            : _v.Transmission.toLocaleLowerCase() === transmission

        const passengerCheck = !passengers.length
          ? true
          : passengers.some(_p => _v.PassengerCapacity >= _p)

        return transmissionCheck && passengerCheck
      }),
    [data, passengers, transmission],
  )

  const { vans, suv, cars } = useMemo(() => {
    return filteredData.reduce((prev, cur) => {
      const className = cur.EHIWebName.toLowerCase()
      const key = className.includes('van')
        ? 'vans'
        : className.includes('suv')
        ? 'suv'
        : 'cars'
      return {
        ...prev,
        // @ts-ignore
        [key]: [...(prev[key] ?? []), cur],
      }
    }, {} as Record<'vans' | 'suv' | 'cars', FleetData[]>)
  }, [filteredData])

  const total = useMemo(() => {
    let _total = filteredData.length
    if (!vehicleClasses.length) return _total
    if (!vehicleClasses.includes('cars')) {
      _total -= cars.length
    }
    if (!vehicleClasses.includes('suvs')) {
      _total -= suv.length
    }
    if (!vehicleClasses.includes('vans')) {
      _total -= vans.length
    }
    return _total
  }, [filteredData, vehicleClasses, cars, suv, vans])

  return (
    <div className="product_list padding-vertical-regular theme--dark-blue">
      <div className="row">
        <div className="column small-12">
          <div className="product_list_header flex-container flex-row-wrap align-middle align-justify">
            <h1 className="white-color">{t('rentalOptionsInYerevan')}</h1>
            <Link href={carHirePath} className="btn btn-purple">
              <span> {t('startAReservation')} </span>
            </Link>
          </div>
        </div>
        <Sidebar />
        <div className="column small-12 medium-8 large-9">
          <div className="product_listing_header flex-container align-justify align-middle">
            <h3 className="white-color">
              {t('vehicleClassOptions', { count: total })}
            </h3>
            <div className="location"></div>
          </div>
          {(!vehicleClasses.length || vehicleClasses.includes('cars')) && (
            <div data-cat="cars" className="product_list_wrapper">
              <h4 className="yellow-color arrow--right">
                {t('countVehicleType', {
                  type: t('cars'),
                  count: cars?.length ?? 0,
                })}
              </h4>
              <div className="row">
                {cars?.map?.(fleet => (
                  <FleetCard key={fleet.id} {...fleet} />
                ))}
              </div>
            </div>
          )}
          {(!vehicleClasses.length || vehicleClasses.includes('suvs')) && (
            <div data-cat="suv" className="product_list_wrapper">
              <h4 className="yellow-color arrow--right">
                {t('countVehicleType', {
                  type: t('suvS'),
                  count: suv?.length ?? 0,
                })}
              </h4>
              <div className="row">
                {suv?.map?.(fleet => (
                  <FleetCard key={fleet.id} {...fleet} />
                ))}
              </div>
            </div>
          )}
          {(!vehicleClasses.length || vehicleClasses.includes('vans')) && (
            <div data-cat="van" className="product_list_wrapper">
              <h4 className="yellow-color arrow--right">
                {t('countVehicleType', {
                  type: t('vans'),
                  count: vans?.length ?? 0,
                })}
              </h4>
              <div className="row">
                {vans?.map?.(fleet => (
                  <FleetCard key={fleet.id} {...fleet} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const FleetContent: React.FC<IFleetData> = data => (
  <FleetProvider>
    <FleetContentView {...data} />
  </FleetProvider>
)
